import { Input, Select, Checkbox } from '../ui/Field';
import { RichTextEditor } from '../ui/RichTextEditor';
import { MediaPicker } from '../ui/MediaPicker';
import { TextListField } from '../ui/TextListField';
import { VideoListField } from '../ui/VideoListField';
import { RelationField } from '../ui/RelationField';
import CoordinatesInput from '../ui/CoordinatesInput';
import type { CoordinatesValue } from '../ui/CoordinatesInput';
import type { FieldDef, VideoItem } from '../../lib/types';
import type { Locale } from '../../lib/collections';
import { fieldHelp, pickLoc } from '../../lib/collections';
import { HelpTooltip } from '../ui/HelpTooltip';

interface Props {
  field: FieldDef;
  value: unknown;                 // ya resuelto al locale activo si localized
  onChange: (value: unknown) => void;
  locale: Locale;
  placeholder?: string;
  locationContext?: { location?: string; city?: string; neighborhood?: string };
  onSyncLocation?: (label: string) => void;
}

// Renderiza UN campo según su `type` (los 9 del contrato). Agnóstico de
// localización: el padre (CollectionForm) le pasa el slice del locale activo.
export function FieldRenderer({ field, value, onChange, locale, placeholder, locationContext, onSyncLocation }: Props) {
  switch (field.type) {
    case 'text': {
      if (field.format === 'phone') {
        return (
          <Input
            type="tel"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value.replace(/[^\d\s+\-().]/g, ''))}
            placeholder={placeholder}
          />
        );
      }
      const inputType = field.format === 'email' ? 'email'
        : field.format === 'url' ? 'url'
        : 'text';
      return (
        <Input
          type={inputType}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      );
    }

    case 'richtext':
      return (
        <RichTextEditor
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v)}
        />
      );

    case 'number':
    case 'price': {
      const blocked = field.type === 'price' ? ['e', 'E', '+', '-'] : ['e', 'E'];
      return (
        <Input
          type="number"
          value={value == null ? '' : String(value)}
          step={field.type === 'price' ? '1' : 'any'}
          placeholder={placeholder}
          onKeyDown={(e) => { if (blocked.includes(e.key)) e.preventDefault(); }}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    }

    case 'boolean':
      return (
        <Checkbox
          label={(
            <span className="inline-flex items-center gap-1.5">
              {field.label?.[locale] ?? field.label?.es ?? field.key}
              {fieldHelp(field, locale) && <HelpTooltip text={fieldHelp(field, locale)} />}
            </span>
          )}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      );

    case 'select':
      return (
        <Select value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>{pickLoc(field.optionLabels?.[opt], locale) || opt}</option>
          ))}
        </Select>
      );

    case 'image':
      return (
        <MediaPicker
          value={value ? [value as string] : []}
          onChange={(urls) => onChange(urls[0] ?? '')}
          multiple={false}
        />
      );

    case 'gallery':
      return (
        <MediaPicker
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(urls) => onChange(urls)}
          multiple
        />
      );

    case 'textlist':
      return (
        <TextListField
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(v) => onChange(v)}
        />
      );

    case 'videolist':
      return (
        <VideoListField
          value={Array.isArray(value) ? (value as VideoItem[]) : []}
          onChange={(v) => onChange(v)}
        />
      );

    case 'relation':
      return (
        <RelationField
          relationTo={field.relationTo ?? ''}
          label={pickLoc(field.label, locale)}
          value={(value as string | string[]) ?? (field.many ? [] : '')}
          onChange={(v) => onChange(v)}
          many={field.many}
          locale={locale}
        />
      );

    case 'coordinates':
      return (
        <CoordinatesInput
          value={value as CoordinatesValue | null}
          onChange={(v) => onChange(v)}
          locationContext={locationContext}
          onSyncLocation={onSyncLocation}
        />
      );

    default:
      return <p className="text-sm text-ui-danger">Tipo de campo no soportado: {field.type}</p>;
  }
}
